import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { decryptFile, unwrapAesKey } from "../helpers/licenseImageCrypto";
import { wrapKeyForAllRecipients } from "../helpers/licenseSharing";

/**
 * CheckImageWidget  (view-only, front + back)
 *
 * Shows decryptable photos for a single LicenseCheck.
 * Upload is done in the "Kontrolle durchführen" form.
 *
 * Props:
 *   - licenseId   {number|string}
 *   - checkId     {number|string}
 *   - apiUrl      {string}
 *   - privateKey  {CryptoKey|null}
 *   - hasServerKey {boolean}
 */
function CheckImageWidget({ licenseId, checkId, apiUrl, privateKey, hasServerKey }) {
  // meta: { front: { exists, hasAccess, isOwner }, back: { ... } }
  const [meta, setMeta] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Per-side state
  const [images, setImages] = useState({ front: null, back: null }); // object URL | null
  const [viewing, setViewing] = useState({ front: false, back: false });
  const [viewError, setViewError] = useState({ front: null, back: null });
  const [deleting, setDeleting] = useState({ front: false, back: false });
  const [resharing, setResharing] = useState({ front: false, back: false });
  const [reshareInfo, setReshareInfo] = useState({ front: null, back: null });

  const authHeader = () => ({ accessToken: localStorage.getItem("accessToken") });

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${apiUrl}/licenses/${licenseId}/check/${checkId}/image/meta`,
        { headers: authHeader() }
      );
      setMeta(data);
    } catch {
      setMeta(null);
    }
  }, [apiUrl, licenseId, checkId]);

  useEffect(() => {
    if (apiUrl && checkId) loadMeta();
  }, [apiUrl, checkId, loadMeta]);

  const handleView = async (side) => {
    if (images[side]) {
      URL.revokeObjectURL(images[side]);
      setImages((p) => ({ ...p, [side]: null }));
      return;
    }
    setViewing((p) => ({ ...p, [side]: true }));
    setViewError((p) => ({ ...p, [side]: null }));
    try {
      const { data } = await axios.get(
        `${apiUrl}/licenses/${licenseId}/check/${checkId}/image?side=${side}`,
        { headers: authHeader() }
      );
      const aesKey = await unwrapAesKey(data.envelope, privateKey);
      const plain = await decryptFile(data.ciphertext, data.iv, aesKey);
      const blob = new Blob([plain], { type: data.mimeType });
      setImages((p) => ({ ...p, [side]: URL.createObjectURL(blob) }));
    } catch {
      setViewError((p) => ({ ...p, [side]: "Entschlüsselung fehlgeschlagen." }));
    } finally {
      setViewing((p) => ({ ...p, [side]: false }));
    }
  };

  // Re-share this photo with every user currently authorized for license access.
  const handleReshare = async (side) => {
    setResharing((p) => ({ ...p, [side]: true }));
    setReshareInfo((p) => ({ ...p, [side]: null }));
    setViewError((p) => ({ ...p, [side]: null }));
    try {
      const { data } = await axios.get(
        `${apiUrl}/licenses/${licenseId}/check/${checkId}/image?side=${side}`,
        { headers: authHeader() }
      );
      const aesKey = await unwrapAesKey(data.envelope, privateKey, { extractable: true, usages: ["decrypt"] });
      const keys = await wrapKeyForAllRecipients(aesKey, apiUrl);
      const { data: res } = await axios.post(
        `${apiUrl}/licenses/${licenseId}/check/${checkId}/image/share?side=${side}`,
        { keys },
        { headers: authHeader() }
      );
      setReshareInfo((p) => ({ ...p, [side]: `Zugriff für ${res.shared} Benutzer aktualisiert.` }));
    } catch {
      setViewError((p) => ({ ...p, [side]: "Zugriff konnte nicht aktualisiert werden." }));
    } finally {
      setResharing((p) => ({ ...p, [side]: false }));
    }
  };

  const handleDelete = async (side) => {
    if (!window.confirm(`${side === "front" ? "Vorderseite" : "Rückseite"} löschen?`)) return;
    setDeleting((p) => ({ ...p, [side]: true }));
    try {
      if (images[side]) { URL.revokeObjectURL(images[side]); setImages((p) => ({ ...p, [side]: null })); }
      await axios.delete(
        `${apiUrl}/licenses/${licenseId}/check/${checkId}/image?side=${side}`,
        { headers: authHeader() }
      );
      await loadMeta();
    } catch { /* ignore */ } finally {
      setDeleting((p) => ({ ...p, [side]: false }));
    }
  };

  if (!meta || !hasServerKey) return null;

  const anyExists = meta.front?.exists || meta.back?.exists;
  if (!anyExists) return null;

  if (!expanded) {
    return (
      <button title="Fotos anzeigen" style={btnStyle} onClick={() => setExpanded(true)}>
        🖼
      </button>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <strong style={{ fontSize: "0.82rem" }}>📷 Kontrollfotos</strong>
        <button style={closeBtnStyle} onClick={() => setExpanded(false)}>✕</button>
      </div>

      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
        {["front", "back"].map((side) => {
          const sideMeta = meta[side];
          const label = side === "front" ? "Vorderseite" : "Rückseite";
          if (!sideMeta?.exists) return null;
          return (
            <div key={side} style={{ flex: "1 1 120px", minWidth: 0 }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#495057", marginBottom: "0.25rem" }}>{label}</div>
              {sideMeta.hasAccess ? (
                <>
                  {viewError[side] && <p style={{ color: "red", fontSize: "0.75rem", margin: "0.2rem 0" }}>{viewError[side]}</p>}
                  <button
                    style={{ ...captureLabelStyle, cursor: "pointer" }}
                    onClick={() => handleView(side)}
                    disabled={viewing[side] || !privateKey}
                    title={!privateKey ? "Private Key noch nicht geladen" : ""}
                  >
                    {viewing[side] ? "…" : images[side] ? "Ausblenden" : "🔓 Anzeigen"}
                  </button>
                  {images[side] && (
                    <img
                      src={images[side]}
                      alt={label}
                      style={{ display: "block", width: "100%", maxHeight: "220px", objectFit: "contain", borderRadius: "4px", margin: "0.4rem 0" }}
                    />
                  )}
                  {sideMeta.isOwner && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem", marginTop: "0.2rem" }}>
                      <button
                        type="button"
                        style={{ fontSize: "0.72rem", color: "#0d6efd", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                        onClick={() => handleReshare(side)}
                        disabled={resharing[side] || !privateKey}
                        title={!privateKey ? "Private Key noch nicht geladen" : "Zugriff für alle Berechtigten aktualisieren"}
                      >
                        {resharing[side] ? "…" : "🔄 Zugriff aktualisieren"}
                      </button>
                      {reshareInfo[side] && <span style={{ fontSize: "0.7rem", color: "#28a745" }}>{reshareInfo[side]}</span>}
                      <button
                        type="button"
                        style={{ fontSize: "0.72rem", color: "#dc3545", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                        onClick={() => handleDelete(side)}
                        disabled={deleting[side]}
                      >
                        {deleting[side] ? "…" : "🗑 Löschen"}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p style={{ fontSize: "0.75rem", color: "#6c757d", margin: 0 }}>Kein Zugriff.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const btnStyle = { background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", padding: "2px 4px", lineHeight: 1 };
const closeBtnStyle = { background: "none", border: "none", cursor: "pointer", fontSize: "0.9rem", color: "#6c757d", padding: "0 2px" };
const panelStyle = { background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "4px", padding: "0.6rem", marginTop: "0.4rem" };
const captureLabelStyle = { display: "inline-block", padding: "0.3rem 0.6rem", fontSize: "0.78rem", background: "#0d6efd", color: "#fff", borderRadius: "4px", border: "none" };

export default CheckImageWidget;
